/**
 * Reconnecting WebSocket for the notification feeds.
 *
 * Why this exists: none of the panels had any reconnect, heartbeat or onclose handling. After a backend deploy, a
 * laptop sleep, a network change or a proxy idle timeout the feed went silent until a full page reload, and a
 * socket opened with a stale token stayed open. This wraps WebSocket with:
 *   - exponential backoff reconnect (with jitter), reset on a successful open;
 *   - reconnect on the browser `online` event and when the tab becomes visible again;
 *   - giving up (onGiveUp) after `maxInitialFailures` consecutive failures if the socket NEVER opened. The backend
 *     refuses a bad/expired token during the handshake (HTTP 403), which the browser reports as close code 1006
 *     before open, so "never opened" is how an auth failure looks; retrying forever would just hammer the server;
 *   - a handle with send(obj) / close() / isOpen(); close() is final and stops all retries and listeners.
 *
 * getUrl() is called on every (re)connect so the CURRENT token is used (tokens were previously read once at
 * module load).
 */
export function createSocket({
  getUrl,
  onOpen,
  onMessage,
  onClose,
  onGiveUp,
  maxInitialFailures = 6,
  baseDelay = 1000,
  maxDelay = 30000,
  WebSocketImpl,
}) {
  const WS = WebSocketImpl || (typeof WebSocket !== "undefined" ? WebSocket : undefined);
  let ws = null;
  let stopped = false;
  let everOpened = false;
  let attempts = 0;
  let timer = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    if (stopped) return;
    attempts += 1;
    if (!everOpened && attempts > maxInitialFailures) {
      if (onGiveUp) onGiveUp();
      return;
    }
    const delay = Math.min(maxDelay, baseDelay * 2 ** (attempts - 1)) + Math.random() * 500;
    clearTimer();
    timer = setTimeout(connect, delay);
  };

  function connect() {
    if (stopped || !WS) return;
    clearTimer();
    let url;
    try {
      url = getUrl();
    } catch (e) {
      url = null;
    }
    if (!url) {
      schedule();
      return;
    }
    let socket;
    try {
      socket = new WS(url);
    } catch (e) {
      schedule();
      return;
    }
    ws = socket;
    socket.onopen = (event) => {
      if (ws !== socket) return;
      attempts = 0;
      everOpened = true;
      if (onOpen) onOpen(handle, event);
    };
    socket.onmessage = (event) => {
      if (ws !== socket || !onMessage) return;
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      onMessage(data, handle);
    };
    socket.onerror = () => {
      // onclose always follows; nothing to do here.
    };
    socket.onclose = (event) => {
      if (ws === socket) ws = null;
      if (onClose) onClose(event, { everOpened });
      if (!stopped) schedule();
    };
  }

  const reconnectNow = () => {
    if (stopped || ws) return;
    attempts = 0;
    clearTimer();
    connect();
  };
  const onOnline = () => reconnectNow();
  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") reconnectNow();
  };
  if (typeof window !== "undefined") window.addEventListener("online", onOnline);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);

  const handle = {
    send(obj) {
      if (ws && ws.readyState === 1) {
        ws.send(typeof obj === "string" ? obj : JSON.stringify(obj));
        return true;
      }
      return false;
    },
    isOpen: () => !!ws && ws.readyState === 1,
    close() {
      stopped = true;
      clearTimer();
      if (typeof window !== "undefined") window.removeEventListener("online", onOnline);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      if (ws) {
        const socket = ws;
        ws = null;
        try {
          socket.close();
        } catch (e) {
          /* already closed */
        }
      }
    },
  };

  connect();
  return handle;
}

export default createSocket;
